import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import API_CONFIG, { apiClient } from "../../config/api";

// Async thunk to fetch current user's permissions (called on login)
export const fetchUserPermissions = createAsyncThunk(
  "rbac/fetchUserPermissions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.AUTH_PERMISSIONS);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch permissions"
      );
    }
  }
);

// Async thunk to fetch permissions matrix (screens + roles with permissions) for admin
export const fetchPermissionsMatrix = createAsyncThunk(
  "rbac/fetchPermissionsMatrix",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.AUTH_PERMISSIONS_MATRIX);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch permissions matrix"
      );
    }
  }
);

// Async thunk to fetch available roles (for dropdowns in user creation)
export const fetchAvailableRoles = createAsyncThunk(
  "rbac/fetchAvailableRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.AUTH_ROLES);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch available roles"
      );
    }
  }
);

// Async thunk to update single role permissions
export const updateRolePermissions = createAsyncThunk(
  "rbac/updateRolePermissions",
  async ({ roleId, permissions }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(
        API_CONFIG.ENDPOINTS.AUTH_ROLE_PERMISSIONS(roleId),
        { permissions }
      );
      return { roleId, permissions, response: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update permissions"
      );
    }
  }
);

const initialState = {
  // Current user's permissions
  userPermissions: null,
  userId: null,
  roleId: null,
  roleName: null,
  
  // All roles with permissions (for admin management)
  roles: [],
  
  // Available roles (for dropdowns in user creation)
  availableRoles: [],
  
  // All screens (for admin management)
  screens: [],
  
  // Loading states
  isLoadingPermissions: false,
  isLoadingMatrix: false,
  isLoadingAvailableRoles: false,
  isSaving: false,
  
  // Error states
  permissionsError: null,
  matrixError: null,
  availableRolesError: null,
  saveError: null,
  
  // Success state
  saveSuccess: false,
  
  // Has the user's permissions been fetched at least once?
  isInitialized: false,
};

const rbacSlice = createSlice({
  name: "rbac",
  initialState,
  reducers: {
    // Clear permissions (on logout)
    clearPermissions: (state) => {
      state.userPermissions = null;
      state.userId = null;
      state.roleId = null;
      state.roleName = null;
      state.isInitialized = false;
    },
    // Clear save status
    clearSaveStatus: (state) => {
      state.saveSuccess = false;
      state.saveError = null;
    },
    // Clear errors
    clearErrors: (state) => {
      state.permissionsError = null;
      state.matrixError = null;
      state.availableRolesError = null;
      state.saveError = null;
    },
    // Update local role permissions (for optimistic UI updates)
    updateLocalRolePermissions: (state, action) => {
      const { roleId, screenId, permissionType, value } = action.payload;
      const roleIndex = state.roles.findIndex(r => r.id === roleId);
      if (roleIndex !== -1) {
        const role = state.roles[roleIndex];
        if (role.permissions && role.permissions[screenId]) {
          role.permissions[screenId][permissionType] = value;
          
          // If enabling write, also enable read
          if (permissionType === 'write' && value) {
            role.permissions[screenId].read = true;
          }
          // If disabling read, also disable write
          if (permissionType === 'read' && !value) {
            role.permissions[screenId].write = false;
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch user permissions
    builder
      .addCase(fetchUserPermissions.pending, (state) => {
        state.isLoadingPermissions = true;
        state.permissionsError = null;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.isLoadingPermissions = false;
        state.userPermissions = action.payload.permissions;
        state.userId = action.payload.userId;
        state.roleId = action.payload.roleId;
        state.roleName = action.payload.roleName;
        state.isInitialized = true;
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        state.isLoadingPermissions = false;
        state.permissionsError = action.payload;
        state.isInitialized = true;
      });

    // Fetch permissions matrix (screens + roles)
    builder
      .addCase(fetchPermissionsMatrix.pending, (state) => {
        state.isLoadingMatrix = true;
        state.matrixError = null;
      })
      .addCase(fetchPermissionsMatrix.fulfilled, (state, action) => {
        state.isLoadingMatrix = false;
        state.screens = action.payload.screens || [];
        state.roles = action.payload.roles || [];
      })
      .addCase(fetchPermissionsMatrix.rejected, (state, action) => {
        state.isLoadingMatrix = false;
        state.matrixError = action.payload;
      });

    // Fetch available roles (for dropdowns)
    builder
      .addCase(fetchAvailableRoles.pending, (state) => {
        state.isLoadingAvailableRoles = true;
        state.availableRolesError = null;
      })
      .addCase(fetchAvailableRoles.fulfilled, (state, action) => {
        state.isLoadingAvailableRoles = false;
        // API returns array directly
        state.availableRoles = action.payload;
      })
      .addCase(fetchAvailableRoles.rejected, (state, action) => {
        state.isLoadingAvailableRoles = false;
        state.availableRolesError = action.payload;
      });

    // Update single role permissions
    builder
      .addCase(updateRolePermissions.pending, (state) => {
        state.isSaving = true;
        state.saveError = null;
        state.saveSuccess = false;
      })
      .addCase(updateRolePermissions.fulfilled, (state, action) => {
        state.isSaving = false;
        state.saveSuccess = true;
        // Update the role in the roles array with new permissions
        const { roleId, permissions } = action.payload;
        const roleIndex = state.roles.findIndex(r => r.id === roleId);
        if (roleIndex !== -1) {
          state.roles[roleIndex].permissions = permissions;
        }
      })
      .addCase(updateRolePermissions.rejected, (state, action) => {
        state.isSaving = false;
        state.saveError = action.payload;
      });
  },
});

export const { 
  clearPermissions, 
  clearSaveStatus, 
  clearErrors,
  updateLocalRolePermissions 
} = rbacSlice.actions;

// Selectors
export const selectUserPermissions = (state) => state.rbac.userPermissions;
export const selectUserRole = (state) => ({
  roleId: state.rbac.roleId,
  roleName: state.rbac.roleName,
});
export const selectIsInitialized = (state) => state.rbac.isInitialized;
export const selectRoles = (state) => state.rbac.roles;
export const selectAvailableRoles = (state) => state.rbac.availableRoles;
export const selectScreens = (state) => state.rbac.screens;
export const selectIsLoadingPermissions = (state) => state.rbac.isLoadingPermissions;
export const selectIsLoadingMatrix = (state) => state.rbac.isLoadingMatrix;
export const selectIsLoadingAvailableRoles = (state) => state.rbac.isLoadingAvailableRoles;
export const selectIsSaving = (state) => state.rbac.isSaving;
export const selectSaveSuccess = (state) => state.rbac.saveSuccess;
export const selectSaveError = (state) => state.rbac.saveError;

// Helper selector to check if user has specific permission
export const selectHasPermission = (screenId, permissionType = 'read') => (state) => {
  const permissions = state.rbac.userPermissions;
  if (!permissions) return false;
  
  const screenPermission = permissions[screenId];
  if (!screenPermission) return false;
  
  return permissionType === 'write' ? screenPermission.write : screenPermission.read;
};

export default rbacSlice.reducer;
